import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";

import css from "./WeeklyMenuComponent.module.css";
import { SliderCalendar } from "../../../ui";
import { menuMenuService } from "../../../services/menu-services/menuMenu.service";
import { cycleMenuService } from "../../../services/menu-services/cycleMenu.service";
import { useInitCalendarDate, getCurrentCycleDay } from "../../../helpers";

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const getNext7Dates = (startDate) => {
  const [year, month, day] = startDate.split("-").map(Number);
  const base = new Date(year, month - 1, day);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base);
    d.setDate(d.getDate() + i);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
};

const formatDateDisplay = (dateString) => {
  const [year, month, day] = dateString.split("-").map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const getCycleDay = (dateStr, startDateStr, lengthDays) => {
  const date = new Date(dateStr);
  const start = new Date(startDateStr);
  const daysSinceStart = Math.round((date - start) / (1000 * 60 * 60 * 24));
  return ((daysSinceStart % lengthDays) + lengthDays) % lengthDays + 1;
};

const getCycleEndDate = (startDateStr, durationMonths) => {
  if (!startDateStr || !durationMonths) return null;
  const d = new Date(startDateStr);
  d.setMonth(d.getMonth() + durationMonths);
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

const WeeklyMenuComponent = () => {
  useInitCalendarDate();

  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const selectedDate = useSelector((state) => state.availableDishes.dataOfDay);
  const menuFormat = useSelector((state) => state.appSettings.menuFormat);
  const cycleConfig = useSelector((state) => state.appSettings.cycleConfig);
  const isPeriodBased = menuFormat === "period_based";

  const [weekData, setWeekData] = useState([]);
  const [loading, setLoading] = useState(false);

  const today = getTodayDateString();
  const currentCycleDay = isPeriodBased ? getCurrentCycleDay(cycleConfig) : null;

  useEffect(() => {
    if (!selectedDate) return;
    const dates = getNext7Dates(selectedDate);
    setLoading(true);

    if (isPeriodBased && cycleConfig?.startDate && cycleConfig?.lengthDays) {
      const cycleEndDate = getCycleEndDate(cycleConfig.startDate, cycleConfig.durationMonths);

      const isInWindow = (date) => {
        if (date < cycleConfig.startDate) return false;
        if (cycleEndDate && date > cycleEndDate) return false;
        return true;
      };

      const activeDates = dates.filter(isInWindow);
      const cycleDayMap = {};
      activeDates.forEach((d) => {
        cycleDayMap[d] = getCycleDay(d, cycleConfig.startDate, cycleConfig.lengthDays);
      });

      const uniqueDays = [...new Set(Object.values(cycleDayMap))];

      if (uniqueDays.length === 0) {
        setWeekData(dates.map((date) => ({ date, cycleDay: null, packs: [], outsideCycle: true })));
        setLoading(false);
        return;
      }

      Promise.all(uniqueDays.map((day) => cycleMenuService.getByDay(day)))
        .then((results) => {
          const cycleDataMap = {};
          uniqueDays.forEach((day, i) => {
            cycleDataMap[day] = (results[i] || []).map((pack) => ({
              ...pack,
              menuDishes: pack.cycleDayMenuDishes || pack.menuDishes || [],
            }));
          });
          setWeekData(dates.map((date) => {
            if (!isInWindow(date)) {
              return { date, cycleDay: null, packs: [], outsideCycle: true };
            }
            const cycleDay = cycleDayMap[date];
            return { date, cycleDay, packs: cycleDataMap[cycleDay] || [], outsideCycle: false };
          }));
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      Promise.all(dates.map((d) => menuMenuService.getAllMenuPacks(d)))
        .then((results) => {
          setWeekData(results.map((packs, i) => ({ date: dates[i], cycleDay: null, packs })));
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [selectedDate, isPeriodBased, cycleConfig]);

  return (
    <section className={css.wrap}>
      <div className={css.blockCalendar}>
        <SliderCalendar />
      </div>

      <div className={css.blockBody}>
        {currentCycleDay && (
          <div className={css.currentCycleBanner}>
            {formatDateDisplay(today)} — {`Day ${currentCycleDay}`} of the cycle
          </div>
        )}
        {loading ? (
          <div className={css.loading}>
            <Spin size="large" />
          </div>
        ) : (
          <div className={css.grid}>
            {weekData.map(({ date, cycleDay, packs, outsideCycle }) => (
              <div
                key={date}
                className={`${css.dayColumn} ${outsideCycle ? css.dayColumnInactive : ''} ${date === today ? css.dayColumnToday : ''}`}
              >
                <div className={css.dayHeader}>
                  {formatDateDisplay(date)}
                  {date === today && <span className={css.todayBadge}>Today</span>}
                  {cycleDay && <div className={css.cycleDayLabel}>Day {cycleDay}</div>}
                </div>
                <div className={css.packsList}>
                  {outsideCycle ? (
                    <div className={css.empty}>Cycle ended</div>
                  ) : packs.length === 0 ? (
                    <div className={css.empty}>No menu</div>
                  ) : (
                    packs.map((pack) => (
                      <div key={pack.id} className={css.pack}>
                        <div className={css.packName}>
                          {pack.name[currentLanguage] || pack.name["en"]}
                        </div>
                        <div className={css.meals}>
                          {pack.meals?.map((meal) => {
                            const matchingDish = pack.menuDishes?.find(
                              (d) => d.mealId === meal.id
                            );
                            return (
                              <div key={meal.id} className={css.meal}>
                                <span className={css.mealName}>
                                  {meal.name[currentLanguage] || meal.name["en"]}
                                </span>
                                <span className={css.dishName}>
                                  {matchingDish
                                    ? matchingDish.dish.name[currentLanguage] ||
                                      matchingDish.dish.name["en"]
                                    : "—"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export { WeeklyMenuComponent };
